import { FormControlLabel, FormLabel, Radio, RadioGroup, TextField } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import Spin from 'components/Spin';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { SingleDatePicker } from 'react-dates';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable, useRetryableObservable } from 'react-use-observable';
import { EMPTY } from 'rxjs';
import { tap } from 'rxjs/operators';
import Error from 'v2/components/Shared/Error';
import MyModal from 'v2/components/Shared/MyModal';
import { dateService } from 'v2/services/date';
import SubscriptionsService from 'v2/services/subscriptions';
import subscriptionsService from 'v2/services/subscriptions';

import type { ICancelReason } from 'v2/interfaces/subscriptions';

import type moment from 'moment';

interface IProps {
  type: 'cancelar' | 'suspender' | 'cancelar_agendado' | 'reativar';
  isOpen: boolean;
  contractId: number;
  onClose: () => void;
}

const useStyles = makeStyles(() => ({
  label: {
    fontWeight: 600,
  },
}));

const OTHER_REASON_ID = 1;

const JustifyModal = memo(({ isOpen, type, contractId, onClose }: IProps) => {
  const classes = useStyles();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedInput, setFocusedInput] = useState(false);
  const [refundJustifyId, setRefundJustifyId] = useState<number | undefined>(undefined);
  const [refundOther, setRefundOther] = useState('');
  const [dateCancel, setDateCancel] = useState(null);
  const [cancelReasons, cancelReasonsError, , retryCancelReasons] = useRetryableObservable(() => {
    if (!isOpen) return EMPTY;
    return subscriptionsService.getSubscriptionCancelReasons();
  }, [isOpen]);
  const [removeAccessDate] = useObservable(() => {
    if (!isOpen || !contractId) return EMPTY;
    return subscriptionsService.getRemoveAccessDate(contractId);
  }, [isOpen, contractId]);

  useEffect(() => {
    if (!isOpen) {
      setFocusedInput(false);
      setRefundJustifyId(null);
      setRefundOther('');
    }
  }, [isOpen]);

  const [suspendSubscription] = useCallbackObservable(() => {
    setIsSubmitting(true);
    const reasonObj = { reason_id: refundJustifyId };

    if (refundJustifyId === OTHER_REASON_ID && !!refundOther) {
      reasonObj['reason_description'] = refundOther;
    }

    return SubscriptionsService.suspendSubscription(contractId, reasonObj).pipe(
      tap(
        () => {
          setIsSubmitting(false);
          toast.success('Pedido de suspensão enviado com sucesso, aguarde alguns minutos para que a assinatura seja suspensa');
          setRefundOther('');
          setRefundJustifyId(undefined);
          onClose();
        },
        () => {
          setIsSubmitting(false);
          toast.error('Falha ao completar a solicitação');
        }
      )
    );
  }, [refundJustifyId, refundOther, contractId]);

  const [reactivateSubscription] = useCallbackObservable(() => {
    setIsSubmitting(true);
    return SubscriptionsService.reactivateSubscription(contractId).pipe(
      tap(
        () => {
          setIsSubmitting(false);
          toast.success('Pedido de reativação enviado com sucesso, aguarde alguns minutos para que a assinatura seja reativada');
          setRefundOther('');
          setRefundJustifyId(undefined);
          onClose();
        },
        () => {
          setIsSubmitting(false);
          toast.error('Falha ao completar a solicitação');
        }
      )
    );
  }, [refundJustifyId, refundOther, contractId]);

  const [cancelSubscription] = useCallbackObservable(() => {
    setIsSubmitting(true);
    const reasonObj = { reason_id: refundJustifyId };

    if (refundJustifyId === OTHER_REASON_ID && !!refundOther) {
      reasonObj['reason_description'] = refundOther;
    }

    return SubscriptionsService.cancelSubscription(contractId, reasonObj).pipe(
      tap(
        () => {
          setIsSubmitting(false);
          toast.success('Pedido de cancelamento enviado com sucesso, aguarde alguns minutos para que a assinatura seja cancelada');
          setRefundOther('');
          setRefundJustifyId(undefined);
          onClose();
        },
        () => {
          setIsSubmitting(false);
          toast.error('Falha ao completar a solicitação');
        }
      )
    );
  }, [refundJustifyId, refundOther, contractId]);

  const [cancelSubscriptionAgend] = useCallbackObservable(() => {
    setIsSubmitting(true);
    const reasonObj = { reason_id: refundJustifyId };

    if (refundJustifyId === OTHER_REASON_ID && !!refundOther) {
      reasonObj['reason_description'] = refundOther;
    }

    const dateCancelFormated = dateCancel.format('YYYY-MM-DD');
    return SubscriptionsService.cancelSubscriptionAgend(contractId, reasonObj, dateCancelFormated).pipe(
      tap(
        () => {
          setIsSubmitting(false);
          toast.success('Agendamento confirmado! Esse contrato será cancelado no dia ' + dateCancel.format('DD/MM/YYYY'));
          setRefundOther('');
          setRefundJustifyId(undefined);
          setDateCancel(null);
          onClose();
        },
        () => {
          setIsSubmitting(false);
          toast.error('Falha ao completar a solicitação');
        }
      )
    );
  }, [refundJustifyId, dateCancel, refundOther, contractId]);

  const handleDateCancel = (value: any) => setDateCancel(value);
  const handleSetFocused = (value: boolean) => setFocusedInput(value);
  const handleRefundJustify = (event: any, value: string) => setRefundJustifyId(Number(value));
  const handleRefundOther = (e: React.ChangeEvent<HTMLInputElement>) => setRefundOther(e.target.value);

  const refundConfirm = useCallback(() => {
    if (type === 'cancelar') {
      cancelSubscription();
      return;
    }
    if (type === 'cancelar_agendado') {
      cancelSubscriptionAgend();
      return;
    }
    if (type === 'reativar') {
      reactivateSubscription();
      return;
    }

    suspendSubscription();
  }, [cancelSubscription, cancelSubscriptionAgend, reactivateSubscription, suspendSubscription, type]);

  const renderButtonOk = useMemo(() => {
    function checkIfShouldDisableBtn() {
      if (!refundJustifyId) return true;
      if (refundJustifyId === 1 && !refundOther) return true;
      if (type === 'cancelar_agendado' && !dateCancel) return true;
      return false;
    }
    const shouldDisabledBtn = checkIfShouldDisableBtn();

    if (type !== 'reativar') {
      return (
        <Button
          variant='contained'
          color='primary'
          type='submit'
          onClick={refundConfirm}
          disabled={shouldDisabledBtn || isSubmitting}
          startIcon={isSubmitting && <CircularProgress size={20} />}
        >
          Confirmar
        </Button>
      );
    }

    return (
      <Button color='primary' variant='contained' onClick={refundConfirm} startIcon={isSubmitting && <CircularProgress size={20} />}>
        Confirmar
      </Button>
    );
  }, [dateCancel, isSubmitting, refundConfirm, refundJustifyId, refundOther, type]);

  if (!isOpen) {
    return null;
  }

  const handleIsOutsideRangeScheduleCancel = (day: moment.Moment) => {
    const today = new Date();
    const isAfterToday = today > day.toDate();
    return isAfterToday;
  };

  return (
    <MyModal
      showOk={false}
      customOkButton={renderButtonOk}
      title={`Deseja realmente ${type === 'cancelar_agendado' ? 'cancelar' : type} esse contrato?`}
      onClose={onClose}
      opened={isOpen}
      submitDisabled={false}
    >
      {isSubmitting ? (
        <Spin message='Por favor aguarde, estamos processando sua solicitação' />
      ) : (
        type !== 'reativar' && (
          <>
            <div>
              {!!cancelReasonsError && <Error onRetry={retryCancelReasons} />}
              {!cancelReasonsError && !cancelReasons && <Spin />}
              {!!cancelReasons && (
                <>
                  <FormLabel className={classes.label}>
                    {(type === 'cancelar' || type === 'suspender') && removeAccessDate
                      ? `Caso confirme ${
                          type === 'cancelar' ? 'o cancelamento' : 'a suspensão'
                        }, o cliente terá acesso ao conteúdo até ${dateService.dateToString(new Date(removeAccessDate), 'dd/MM/yyyy')}. `
                      : ''}
                    Por gentileza informe o motivo principal:
                  </FormLabel>
                  <br />
                  <RadioGroup
                    aria-label='Por gentileza informe o motivo principal:'
                    name='refund'
                    value={refundJustifyId}
                    onChange={handleRefundJustify}
                  >
                    {cancelReasons?.map((reason: ICancelReason) => (
                      <FormControlLabel key={reason.id} value={reason.id} control={<Radio />} label={reason.name} />
                    ))}
                    {refundJustifyId === OTHER_REASON_ID && (
                      <TextField
                        multiline={true}
                        fullWidth={true}
                        InputProps={{ rows: 3, disableUnderline: true }}
                        InputLabelProps={{ shrink: true }}
                        placeholder='Informe o motivo'
                        onChange={handleRefundOther}
                        value={refundOther}
                      />
                    )}
                  </RadioGroup>
                </>
              )}
            </div>

            {type === 'cancelar_agendado' && (
              <>
                <div className='m24' />
                <FormLabel htmlFor='date_cancel'>Informe a data que deseja que o contrato seja cancelado:</FormLabel>
                <div className='m8' />
                <SingleDatePicker
                  date={dateCancel}
                  onDateChange={handleDateCancel}
                  focused={focusedInput}
                  onFocusChange={({ focused }) => handleSetFocused(focused)}
                  id='date_cancel'
                  numberOfMonths={1}
                  isOutsideRange={handleIsOutsideRangeScheduleCancel}
                  openDirection='up'
                  placeholder='Data'
                  daySize={30}
                  small
                  required
                  hideKeyboardShortcutsPanel
                />
              </>
            )}
          </>
        )
      )}
    </MyModal>
  );
});

export default JustifyModal;
